import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoanEstimator from './LoanEstimator';
import '../styles/LoanForm.css';

function LoanerCreateLoan({ onSuccess, requestToConvert = null }) {
  const { currentUser } = useAuth();
  const [loanees, setLoanees] = useState([]);
  const [formData, setFormData] = useState({
    loaneeId: requestToConvert?.loaneeId || '',
    amount: requestToConvert?.amount || '',
    purpose: requestToConvert?.purpose || '',
    notes: requestToConvert?.rejectionReason 
      ? `Previous rejection reason: ${requestToConvert.rejectionReason}\n\n${requestToConvert?.notes || ''}` 
      : (requestToConvert?.notes || ''),
    startDate: new Date().toISOString().split('T')[0] // Default to today
  });
  const [estimateData, setEstimateData] = useState(null);
  const [showEstimator, setShowEstimator] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLoanees();
  }, []);

  async function fetchLoanees() {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'loanee')
      );
      const snapshot = await getDocs(usersQuery);
      const loaneeList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLoanees(loaneeList);
    } catch (err) {
      console.error('Error fetching loanees:', err);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleEstimateComplete(estimate) {
    setEstimateData(estimate);
    setShowEstimator(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.loaneeId) {
      setError('Please select a loanee');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid loan amount');
      return;
    }

    if (!estimateData) {
      setError('Please calculate loan terms using the estimator');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const selectedLoanee = loanees.find(l => l.id === formData.loaneeId);

      await addDoc(collection(db, 'loans'), {
        loanerId: currentUser.uid,
        loanerName: currentUser.displayName,
        loaneeId: formData.loaneeId,
        loaneeName: selectedLoanee?.displayName || 'Unknown',
        loaneeEmail: selectedLoanee?.email || '',
        amount: parseFloat(formData.amount),
        purpose: formData.purpose,
        notes: formData.notes,
        startDate: formData.startDate,
        interestRate: estimateData.interestRate,
        termLength: estimateData.termLength,
        termType: estimateData.termType,
        monthlyPayment: estimateData.monthlyPayment,
        totalInterest: estimateData.totalInterest,
        amortizationSchedule: estimateData.amortizationSchedule,
        status: 'pending_acceptance',
        remainingBalance: parseFloat(formData.amount),
        payments: [],
        createdAt: new Date().toISOString(),
        convertedFromRequestId: requestToConvert?.id || null
      });

      setFormData({ 
        loaneeId: '', 
        amount: '', 
        purpose: '', 
        notes: '', 
        startDate: new Date().toISOString().split('T')[0] 
      });
      setEstimateData(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to create loan');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="loan-form-container">
      <h2>{requestToConvert ? 'Convert Request to Loan' : 'Create New Loan'}</h2>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="loan-form">
        <div className="input-group">
          <label htmlFor="loaneeId">Loanee</label>
          <select
            id="loaneeId"
            name="loaneeId"
            value={formData.loaneeId}
            onChange={handleChange}
            className="select-field"
            required
            disabled={!!requestToConvert}
          >
            <option value="">Select a loanee</option>
            {loanees.map(loanee => (
              <option key={loanee.id} value={loanee.id}>
                {loanee.displayName} ({loanee.email})
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="amount">Loan Amount ($)</label>
          <input
            id="amount"
            name="amount"
            type="number"
            step="0.01"
            min="0"
            value={formData.amount}
            onChange={handleChange}
            className="input-field"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="purpose">Purpose</label>
          <input
            id="purpose"
            name="purpose"
            type="text"
            value={formData.purpose}
            onChange={handleChange}
            className="input-field"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="startDate">First Payment Date</label>
          <input
            id="startDate"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
            className="input-field"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="textarea-field"
            rows="4"
          />
        </div>

        {!showEstimator && !estimateData && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowEstimator(true)}
            disabled={!formData.amount || parseFloat(formData.amount) <= 0}
          >
            Calculate Loan Terms
          </button>
        )}

        {showEstimator && (
          <LoanEstimator
            loanAmount={formData.amount}
            startDate={formData.startDate}
            onEstimateComplete={handleEstimateComplete}
          />
        )}

        {estimateData && !showEstimator && (
          <div className="estimate-summary-box">
            <h4>Loan Terms Summary</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Interest Rate:</span>
                <span className="value">{estimateData.interestRate}%</span>
              </div>
              <div className="summary-item">
                <span className="label">Term:</span>
                <span className="value">{estimateData.termLength} {estimateData.termType}</span>
              </div>
              <div className="summary-item">
                <span className="label">Monthly Payment:</span>
                <span className="value">${estimateData.monthlyPayment.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Total Interest:</span>
                <span className="value">${estimateData.totalInterest.toFixed(2)}</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setShowEstimator(true)}
            >
              Recalculate Terms
            </button>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !estimateData}
        >
          {loading ? 'Sending...' : 'Send for Acceptance'}
        </button>
      </form>
    </div>
  );
}

export default LoanerCreateLoan;
